import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Settings,
  X,
  LayoutDashboard,
} from "lucide-react";
import { ReactComponent as Logo } from "../../assets/logo.svg";
import { ReactComponent as UserIcon } from "../../assets/ic_user.svg";
import { ReactComponent as CoinIcon } from "../../assets/ic_coin.svg";
import { ReactComponent as HomeIcon } from "../../assets/ic_home.svg";
import { ReactComponent as SurveillanceIcon } from "../../assets/ic_eye.svg";
import { ReactComponent as CountryIcon } from "../../assets/ic_globe.svg";
import { ReactComponent as StaffIcon } from "../../assets/ic_user_box.svg";
import { ReactComponent as RoleIcon } from "../../assets/ic_hash.svg";
import { BodySmallMedium } from "../ui/Typography";
import { useAuth } from "../../contexts/AuthContext";

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  isParent?: boolean;
  children?: MenuItem[];
  requiredPermission?: {
    action: string;
    tableName: string;
  };
}

interface SidebarProps {
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (isOpen: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isMobileMenuOpen = false,
  setIsMobileMenuOpen,
}) => {
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { hasPermission, sidebarData } = useAuth();

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId],
    );
  };

  // Hardcoded menu structure
  const allMenuItems: MenuItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <LayoutDashboard className="w-5 h-5 mr-3" />,
      path: "/dashboard",
    },
    {
      id: "reception",
      label: "Reception",
      icon: <UserIcon className="w-5 h-5 mr-3" />,
      isParent: true,
      children: [
        {
          id: "customer-list",
          label: "Customer List",
          icon: <UserIcon className="w-5 h-5 mr-3" />,
          path: "/customers",
          requiredPermission: { action: "READ", tableName: "customers" },
        },
        {
          id: "daily-entry-list",
          label: "Daily Entry",
          icon: <UserIcon className="w-5 h-5 mr-3" />,
          path: "/daily-entries",
          requiredPermission: { action: "READ", tableName: "daily_entries" },
        },
      ],
    },
    {
      id: "cash-exchange",
      label: "Cash Exchange",
      icon: <CoinIcon className="w-5 h-5 mr-3" />,
      isParent: true,
      children: [
        {
          id: "cash-in",
          label: "Cash In",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/cash-in",
          requiredPermission: { action: "READ", tableName: "cash_in" },
        },
        {
          id: "cash-out",
          label: "Cash Out",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/cash-out",
          requiredPermission: { action: "READ", tableName: "cash_out" },
        },
        {
          id: "cash-records",
          label: "Cash Records",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/cash-records",
        },
        {
          id: "fill-transaction",
          label: "Fill Transaction",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/fill-transaction",
        },
        {
          id: "commision-type",
          label: "Commission Types",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/commission-types",
        },
        {
          id: "player-commissions",
          label: "Player Commissions",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/player-commissions",
        },
      ],
    },
    {
      id: "settings",
      label: "Settings",
      icon: <Settings className="w-5 h-5 mr-3" />,
      isParent: true,
      children: [
        {
          id: "agents-list",
          label: "Agents",
          icon: <HomeIcon className="w-5 h-5 mr-3" />,
          path: "/agents",
          requiredPermission: { action: "READ", tableName: "agents" },
        },
        {
          id: "countries",
          label: "Countries",
          icon: <CountryIcon className="w-5 h-5 mr-3" />,
          path: "/countries",
          requiredPermission: { action: "READ", tableName: "countries" },
        },
        {
          id: "staffs",
          label: "Staffs",
          icon: <StaffIcon className="w-5 h-5 mr-3" />,
          path: "/staffs",
          requiredPermission: { action: "READ", tableName: "users" },
        },
        {
          id: "surveillance",
          label: "Surveillance",
          icon: <SurveillanceIcon className="w-5 h-5 mr-3" />,
          path: "/surveillance",
        },
        {
          id: "roles-permissions",
          label: "Roles & Permissions",
          icon: <RoleIcon className="w-5 h-5 mr-3" />,
          path: "/roles",
          requiredPermission: { action: "READ", tableName: "roles" },
        },
        {
          id: "menu",
          label: "Menu",
          icon: <RoleIcon className="w-5 h-5 mr-3" />,
          path: "/menu-bar",
          requiredPermission: { action: "READ", tableName: "sidebar_menu" },
        },
        {
          id: "facilities-categories",
          label: "Facilities Categories",
          icon: <HomeIcon className="w-5 h-5 mr-3" />,
          path: "/facilities-categories",
        },
        {
          id: "audit-logs",
          label: "Audit Logs",
          icon: <Settings className="w-5 h-5 mr-3" />,
          path: "/audit-logs",
          requiredPermission: { action: "READ", tableName: "audit_logs" },
        },
      ],
    },
    {
      id: "games",
      label: "Games",
      icon: <CoinIcon className="w-5 h-5 mr-3" />,
      isParent: true,
      children: [
        {
          id: "game-list",
          label: "Game List",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/games",
          requiredPermission: { action: "READ", tableName: "games" },
        },
        {
          id: "game-table",
          label: "Game Table",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/game-tables",
          requiredPermission: { action: "READ", tableName: "game_tables" },
        },
        {
          id: "game-records",
          label: "Game Records",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/game-records",
          requiredPermission: { action: "READ", tableName: "game_records" },
        },
      ],
    },
    {
      id: "blacklisted-players",
      label: "Blacklisted Players",
      icon: <RoleIcon className="w-5 h-5 mr-3" />,
      path: "/blacklisted-players",
      requiredPermission: { action: "READ", tableName: "ban_requests" },
    },
    {
      id: "commission",
      label: "Commission",
      icon: <CoinIcon className="w-5 h-5 mr-3" />,
      isParent: true,
      children: [
        {
          id: "package-types",
          label: "Package Types",
          icon: <CoinIcon className="w-5 h-5 mr-3" />,
          path: "/package-types",
        },
        {
          id: "agent-assignments",
          label: "Agent Assignments",
          icon: <UserIcon className="w-5 h-5 mr-3" />,
          path: "/agent-assignments",
        },
        {
          id: "facilities-categories",
          label: "Facilities Categories",
          icon: <HomeIcon className="w-5 h-5 mr-3" />,
          path: "/facilities-categories",
        },
        {
          id: "commission-tracking",
          label: "Commission Tracking",
          icon: <SurveillanceIcon className="w-5 h-5 mr-3" />,
          path: "/commission-tracking",
        },
      ],
    },
  ];

  // Filter menu items based on backend menus
  const menuItems: MenuItem[] = React.useMemo(() => {
    // If no sidebar data yet, show all menu items (loading state)
    if (!sidebarData) return allMenuItems;

    // If we have sidebar data but no menus, show no items
    if (!sidebarData.menus || sidebarData.menus.length === 0) {
      return [];
    }

    // Extract allowed route paths from the menus array
    const allowedRoutes = sidebarData.menus.map((menu) => menu.route_path);

    // Debug: Log allowed routes
    console.log("Allowed routes from sidebar data:", allowedRoutes);

    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
      const filteredItems = items
        .map((item) => {
          if (item.isParent && item.children) {
            // For parent items, recursively filter children first
            const allowedChildren = filterMenuItems(item.children);
            console.log(
              `Parent "${item.label}" has ${allowedChildren.length} allowed children:`,
              allowedChildren.map((c) => c.label),
            );

            // Only return parent if it has allowed children
            if (allowedChildren.length > 0) {
              return { ...item, children: allowedChildren };
            }
            return null;
          } else {
            // For regular items, check if the route is in allowedRoutes
            const isAllowed = item.path
              ? allowedRoutes.includes(item.path)
              : false;
            console.log(
              `Checking route "${item.label}" (${item.path}):`,
              isAllowed,
            );
            return isAllowed ? item : null;
          }
        })
        .filter((item): item is MenuItem => item !== null);

      console.log(
        "Filtered items result:",
        filteredItems.map((i) => ({
          label: i.label,
          isParent: i.isParent,
          childrenCount: i.children?.length || 0,
        })),
      );
      return filteredItems;
    };

    return filterMenuItems(allMenuItems);
  }, [sidebarData]);

  const isExpanded = (menuId: string) => expandedMenus.includes(menuId);

  const hasMenuAccess = (item: MenuItem): boolean => {
    if (!item.requiredPermission) return true;
    return hasPermission(
      item.requiredPermission.action,
      item.requiredPermission.tableName,
    );
  };

  const getAccessibleChildren = (children: MenuItem[]): MenuItem[] => {
    if (!sidebarData?.menus) return [];
    const allowedRoutes = sidebarData.menus.map((menu) => menu.route_path);
    return children.filter((child) =>
      child.path ? allowedRoutes.includes(child.path) : false,
    );
  };

  // menuItems are already properly filtered, no need for additional filtering
  const visibleMenuItems = menuItems;

  // Auto-expand parent menus that have accessible children
  React.useEffect(() => {
    if (sidebarData?.menus) {
      const allowedRoutes = sidebarData.menus.map((menu) => menu.route_path);
      const parentsToExpand: string[] = [];

      allMenuItems.forEach((item) => {
        if (item.isParent && item.children) {
          const accessibleChildren = item.children.filter((child) =>
            child.path ? allowedRoutes.includes(child.path) : false,
          );
          console.log(`Auto-expand check for "${item.label}":`, {
            totalChildren: item.children.length,
            accessibleChildren: accessibleChildren.length,
            accessibleRoutes: accessibleChildren.map((c) => c.path),
          });

          if (accessibleChildren.length > 0) {
            parentsToExpand.push(item.id);
          }
        }
      });

      console.log("Auto-expanding parent menus:", parentsToExpand);
      setExpandedMenus((prev) => {
        const newExpanded = [...new Set([...prev, ...parentsToExpand])];
        return newExpanded;
      });
    }
  }, [sidebarData?.menus]);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen?.(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 h-screen bg-background flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        {/* Logo section with close button for mobile */}
        <div className="align-center px-6 py-3 border-b border-slate-700 flex items-center w-full">
          <Logo className="w-32 h-16" />
          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen?.(false)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Navigation section */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
          <div className="p-4 space-y-2">
            {visibleMenuItems.length === 0 ? (
              // No menu items available
              <div className="flex items-center justify-center py-8">
                <div className="text-slate-400">No menu items available</div>
              </div>
            ) : (
              visibleMenuItems.map((item) => (
                <div key={item.id}>
                  {item.isParent ? (
                    <>
                      <button
                        onClick={() => toggleMenu(item.id)}
                        className="w-full flex items-center justify-between px-6 py-3 text-left transition-colors rounded-full text-slate-300 hover:bg-slate-700 hover:text-white"
                      >
                        <div className="flex items-center min-w-0">
                          {item.icon}
                          <BodySmallMedium className="truncate">
                            {item.label}
                          </BodySmallMedium>
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          {isExpanded(item.id) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      </button>

                      {isExpanded(item.id) && item.children && (
                        <div className="ml-8 mt-2 space-y-1">
                          {(() => {
                            const accessibleChildren = getAccessibleChildren(
                              item.children,
                            );
                            console.log(
                              `Rendering children for "${item.label}":`,
                              {
                                isExpanded: isExpanded(item.id),
                                totalChildren: item.children.length,
                                accessibleChildren: accessibleChildren.length,
                                childrenLabels: accessibleChildren.map(
                                  (c) => c.label,
                                ),
                              },
                            );
                            return accessibleChildren;
                          })().map((child) => (
                            <NavLink
                              key={child.id}
                              to={child.path!}
                              className={({ isActive }) =>
                                `flex items-center px-4 py-2 text-left transition-colors rounded-lg ${
                                  isActive
                                    ? "bg-slate-600 text-white"
                                    : "text-slate-400 hover:bg-slate-700 hover:text-white"
                                }`
                              }
                            >
                              <div className="flex items-center min-w-0">
                                {child.icon}
                                <BodySmallMedium className="truncate">
                                  {child.label}
                                </BodySmallMedium>
                              </div>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    hasMenuAccess(item) && (
                      <NavLink
                        to={item.path!}
                        className={({ isActive }) =>
                          `flex items-center px-6 py-3 text-left transition-colors rounded-full ${
                            isActive
                              ? "bg-slate-700 text-white"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`
                        }
                      >
                        <div className="flex items-center min-w-0">
                          {item.icon}
                          <BodySmallMedium className="truncate">
                            {item.label}
                          </BodySmallMedium>
                        </div>
                      </NavLink>
                    )
                  )}
                </div>
              ))
            )}
          </div>
        </nav>
      </div>
    </>
  );
};
